import { controlPlaneCall } from './client'
import { ensureFreshAdministrationToken } from './refresh'

/**
 * Superadmin grant management against the Control Plane's SuperadminService
 * (administration audience + step-up confirmation on every create/delete).
 * The console is only a client: authority, the audit fact, and the projection
 * that flips `User.isSuperadmin` all stay on the Control Plane.
 */

const SERVICE = 'SuperadminService'

export interface SuperadminGrant {
  readonly id: string
  readonly identityId: string
  readonly permissions: readonly string[]
  readonly grantReason: string
  readonly createTime: string | null
}

export interface ControlPlaneErrorShape {
  readonly code: string
  readonly reason: string
  readonly metadata: Readonly<Record<string, string>>
}

export class ControlPlaneError extends Error implements ControlPlaneErrorShape {
  readonly code: string
  readonly reason: string
  readonly metadata: Readonly<Record<string, string>>

  constructor(shape: ControlPlaneErrorShape, message: string) {
    super(message)
    this.name = 'ControlPlaneError'
    this.code = shape.code
    this.reason = shape.reason
    this.metadata = shape.metadata
  }

  get confirmUrl(): string | null {
    return this.reason === 'CONFIRMATION_REQUIRED' ? (this.metadata.confirm_url ?? null) : null
  }
}

const ERROR_DETAIL_TYPE = 'connectrpc.identity.v1.ErrorDetail'

/** ErrorDetail.Reason enum values (identity.proto); names are what callers switch on. */
const REASON_NAMES: Record<number, string> = {
  1: 'NO_ACTIVE_MEMBERSHIP',
  2: 'CONFIRMATION_REQUIRED',
  3: 'INVALID_CREDENTIAL',
  4: 'REFRESH_FAMILY_REVOKED',
  5: 'REFRESH_REPLAY_MISMATCH',
  6: 'VERSION_CONFLICT',
  11: 'SELF_TARGET_FORBIDDEN',
  14: 'IDENTITY_SUSPENDED',
  17: 'SUPERADMIN_MEMBERSHIP_CONFLICT',
}

interface ConnectErrorBody {
  code?: string
  message?: string
  details?: Array<{ type?: string; value?: string; debug?: unknown }>
}

function base64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/**
 * Minimal protobuf reader for ErrorDetail (`reason` = field 1 varint,
 * `metadata` = field 2 map<string,string>). The console carries no protobuf
 * runtime, and this is the only message it ever decodes.
 */
function decodeErrorDetail(bytes: Uint8Array): { reason: string; metadata: Record<string, string> } {
  const text = new TextDecoder()
  let pos = 0
  const varint = (): number => {
    let result = 0
    let shift = 0
    for (;;) {
      const b = bytes[pos++]
      if (b === undefined) throw new Error('truncated varint')
      result += (b & 0x7f) * 2 ** shift
      if ((b & 0x80) === 0) return result
      shift += 7
    }
  }
  const bytesField = (): Uint8Array => {
    const len = varint()
    const out = bytes.subarray(pos, pos + len)
    pos += len
    return out
  }
  const skip = (wireType: number): void => {
    if (wireType === 0) varint()
    else if (wireType === 2) bytesField()
    else if (wireType === 1) pos += 8
    else if (wireType === 5) pos += 4
    else throw new Error(`unsupported wire type ${wireType}`)
  }
  const mapEntry = (entry: Uint8Array): [string, string] => {
    const saved = bytes
    const savedPos = pos
    bytes = entry
    pos = 0
    let key = ''
    let value = ''
    while (pos < bytes.length) {
      const tag = varint()
      const field = tag >>> 3
      const wire = tag & 7
      if (field === 1 && wire === 2) key = text.decode(bytesField())
      else if (field === 2 && wire === 2) value = text.decode(bytesField())
      else skip(wire)
    }
    bytes = saved
    pos = savedPos
    return [key, value]
  }

  let reasonNumber = 0
  const metadata: Record<string, string> = {}
  while (pos < bytes.length) {
    const tag = varint()
    const field = tag >>> 3
    const wire = tag & 7
    if (field === 1 && wire === 0) reasonNumber = varint()
    else if (field === 2 && wire === 2) {
      const [k, v] = mapEntry(bytesField())
      metadata[k] = v
    } else skip(wire)
  }
  const reason = reasonNumber === 0 ? '' : (REASON_NAMES[reasonNumber] ?? `REASON_${reasonNumber}`)
  return { reason, metadata }
}

function detailFromDebug(debug: unknown): { reason: string; metadata: Record<string, string> } | null {
  if (typeof debug !== 'object' || debug === null) return null
  const d = debug as { reason?: unknown; metadata?: unknown }
  const reason = typeof d.reason === 'string' ? d.reason : ''
  const metadata: Record<string, string> = {}
  if (typeof d.metadata === 'object' && d.metadata !== null) {
    for (const [k, v] of Object.entries(d.metadata as Record<string, unknown>)) {
      if (typeof v === 'string') metadata[k] = v
    }
  }
  return { reason, metadata }
}

export function parseControlPlaneError(status: number, body: ConnectErrorBody | null): ControlPlaneError {
  const code = body?.code ?? (status === 401 ? 'unauthenticated' : 'unknown')
  let reason = ''
  let metadata: Record<string, string> = {}
  const detail = body?.details?.find(d => d.type === ERROR_DETAIL_TYPE)
  if (detail) {
    const fromDebug = detailFromDebug(detail.debug)
    if (fromDebug) {
      ;({ reason, metadata } = fromDebug)
    } else if (detail.value) {
      try {
        ;({ reason, metadata } = decodeErrorDetail(base64ToBytes(detail.value)))
      } catch {
        // Undecodable detail ⇒ code-level handling only.
      }
    }
  }
  return new ControlPlaneError(
    { code, reason, metadata },
    body?.message || `Control Plane call failed (${code})`,
  )
}

async function call<T>(method: string, body: unknown): Promise<T> {
  const bearer = await ensureFreshAdministrationToken()
  if (!bearer) {
    throw new ControlPlaneError(
      { code: 'unauthenticated', reason: '', metadata: {} },
      'No Control Plane session',
    )
  }
  const res = await controlPlaneCall(SERVICE, method, body, bearer)
  if (!res.ok) {
    let parsed: ConnectErrorBody | null = null
    try {
      parsed = (await res.json()) as ConnectErrorBody
    } catch {
      parsed = null
    }
    throw parseControlPlaneError(res.status, parsed)
  }
  return (await res.json()) as T
}

function toGrant(raw: unknown): SuperadminGrant | null {
  if (typeof raw !== 'object' || raw === null) return null
  const g = raw as Record<string, unknown>
  if (typeof g.id !== 'string' || typeof g.identityId !== 'string') return null
  return {
    id: g.id,
    identityId: g.identityId,
    permissions: Array.isArray(g.permissions) ? g.permissions.filter((p): p is string => typeof p === 'string') : [],
    grantReason: typeof g.grantReason === 'string' ? g.grantReason : '',
    createTime: typeof g.createTime === 'string' ? g.createTime : null,
  }
}

export async function listSuperadminGrants(): Promise<SuperadminGrant[]> {
  const body = await call<{ grants?: unknown[] }>('ListSuperadminGrants', {})
  return (body.grants ?? []).map(toGrant).filter((g): g is SuperadminGrant => g !== null)
}

export function newRequestId(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (c?.randomUUID) return c.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** `requestId` must be reused when retrying after a step-up confirmation. */
export async function createSuperadminGrant(input: {
  identityId: string
  reason: string
  requestId: string
}): Promise<SuperadminGrant | null> {
  const body = await call<unknown>('CreateSuperadminGrant', {
    identityId: input.identityId,
    reason: input.reason,
    requestId: input.requestId,
  })
  return toGrant(body)
}

export async function deleteSuperadminGrant(input: {
  grantId: string
  reason: string
  requestId: string
}): Promise<void> {
  await call<unknown>('DeleteSuperadminGrant', {
    grantId: input.grantId,
    reason: input.reason,
    requestId: input.requestId,
  })
}

/**
 * Open the server-minted step-up URL in a popup and resolve when it closes so
 * the caller can retry the same request in place. Falls back to a full-page
 * redirect when the popup is blocked (the navigation takes over).
 */
export function openConfirmation(url: string): Promise<void> {
  const popup = window.open(url, 'libra-confirm', 'popup,width=480,height=680')
  if (!popup) {
    window.location.assign(url)
    return new Promise<void>(() => {})
  }
  return new Promise<void>(resolve => {
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer)
        resolve()
      }
    }, 500)
  })
}

/**
 * Run a grant mutation, driving one step-up confirmation round if the Control
 * Plane asks for it. The mutation is retried once with the same request id.
 */
export async function withStepUp<T>(
  mutation: () => Promise<T>,
  onConfirmationNeeded?: () => void,
): Promise<T> {
  try {
    return await mutation()
  } catch (error) {
    const confirmUrl = error instanceof ControlPlaneError ? error.confirmUrl : null
    if (!confirmUrl) throw error
    onConfirmationNeeded?.()
    await openConfirmation(confirmUrl)
    return mutation()
  }
}
